import httpClient from '@/lib/httpClient'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

export type FeVisibility = 'lock' | 'hide'

export interface PlanFeature {
  lookup_key: string
  name: string
  available: boolean
  value: string | null
  fe_visibility: FeVisibility
  upsell_modal: boolean
}

export interface BillingSubscription {
  has_active_subscription: boolean
  team_type?: string
  subscription: {
    plan_name: string
    status: string
    is_trialing: boolean
    trial_ends_at: string | null
    current_period_end: string | null
  } | null
  features: PlanFeature[]
}

export const billingSubscriptionQueryKey = (teamId?: string | number | null) => [
  'billing-subscription',
  teamId ?? null,
]

export default function useGetBillingSubscription({
  teamId,
  enabled = true,
  refetchOnMount,
}: {
  teamId?: string | number | null
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
}) {
  // Set right before refetchForce() and read once by the next queryFn run —
  // refetch() itself always hits the network (bypassing React Query's own
  // cache), but the backend additionally caches this response for 24h per
  // team, so a plain refetch() can still come back stale. refresh_cache=1
  // busts that server-side cache too; only sent when explicitly forced, not
  // on every normal fetch, so we don't defeat the point of caching it.
  const forceRefresh = useRef(false)

  const query = useQuery<BillingSubscription>({
    queryKey: billingSubscriptionQueryKey(teamId),
    queryFn: async () => {
      const params = forceRefresh.current ? { refresh_cache: 1 } : undefined
      forceRefresh.current = false
      const response = await httpClient.get('billing/subscription', {
        headers: teamId ? { 'X-Team': String(teamId) } : undefined,
        params,
      })
      return response.data
    },
    enabled: enabled && !!teamId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount,
  })

  const refetchForce = () => {
    forceRefresh.current = true
    return query.refetch()
  }

  return { ...query, refetchForce }
}
